/**
 * My submissions — every demand the current user submitted, at any stage, so a
 * submitter (or a co-lead guiding them) can track where each request is in the
 * pipeline. Backed by GET /demands?mine_submissions=true; open to any signed-in
 * user (a submitter can be any role).
 */

import { useMemo, useState } from 'react'
import {
  Badge,
  Button,
  Caption1,
  Dropdown,
  Option,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  Title2,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { ArrowDownload20Regular } from '@fluentui/react-icons'
import { Link, useNavigate } from 'react-router-dom'
import { useDemands } from '../hooks/useDemands'
import { focusAreaWithName } from '../types/focus_area'
import { useColumnSort, sortByCriteria } from '../hooks/useColumnSort'
import { ErrorCard } from '../components/ErrorCard'
import { SortableHeader } from '../components/SortableHeader'
import { useToast } from '../components/ToastProvider'
import api from '../lib/api'
import { downloadBlob } from '../lib/download'
import { apiErrorMessage, blobErrorMessage } from '../lib/apiError'
import { LEGACY_STAGES, LIVE_STAGES, STAGE_LABELS } from '../lib/labels'
import type { Demand, DemandStage } from '../types/demand'
import { TitleWithHelp } from '../components/TitleWithHelp'

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' },
  toolbar: { display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'center', flexWrap: 'wrap' },
  wrap: {
    background: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    overflowX: 'auto',
  },
  row: { cursor: 'pointer' },
  muted: { color: tokens.colorNeutralForeground3 },
  state: { padding: '48px 16px', textAlign: 'center', color: tokens.colorNeutralForeground3 },
})

/**
 * Sort order for the stage column, derived from the canonical lists rather than
 * restated here.
 *
 * The map used to hold the pre-v2.8.38 vocabulary verbatim. Every current stage
 * fell through to the fallback, so the comparator returned 0 for every pair and
 * the sort was a no-op — the header drew its arrow over rows that never moved,
 * while the filter dropdown twenty lines below already used the new names.
 * Deriving it means the next rename breaks the build instead of the sort.
 *
 * Legacy stages sort after the live ones instead of sharing the fallback, so a
 * stale row still lands somewhere deterministic.
 */
const SUB_STAGE_ORDER: Record<string, number> = Object.fromEntries([
  ...LIVE_STAGES.map((s, i) => [s, i] as const),
  ...LEGACY_STAGES.map((s, i) => [s, LIVE_STAGES.length + i] as const),
])
const SUB_STAGE_UNKNOWN = LIVE_STAGES.length + LEGACY_STAGES.length
type SubSortKey = 'title' | 'fa' | 'stage' | 'submitted' | 'age'

function submittedTs(d: Demand): number {
  const raw = d.submission_date ?? d.created_at
  const t = raw ? new Date(raw).getTime() : 0
  return Number.isNaN(t) ? 0 : t
}

function submittedDate(d: Demand): string {
  const raw = d.submission_date ?? d.created_at
  return raw ? new Date(raw).toLocaleDateString() : '—'
}

/** Days the demand has sat in its current stage — the same "how long has this
 * been waiting" signal the main demand list shows, so submitters read their
 * rows the same way. */
function ageDays(d: Demand): number {
  const anchor = d.stage_entered_at ?? d.submission_date ?? d.created_at
  if (!anchor) return 0
  const then = new Date(anchor).getTime()
  if (Number.isNaN(then)) return 0
  return Math.max(0, Math.floor((Date.now() - then) / 86_400_000))
}

export function compareSubmission(a: Demand, b: Demand, key: SubSortKey): number {
  switch (key) {
    case 'title':
      return a.title.localeCompare(b.title)
    case 'fa':
      return (a.focus_area_code ?? '').localeCompare(b.focus_area_code ?? '')
    case 'stage':
      return (
        (SUB_STAGE_ORDER[a.stage ?? ''] ?? SUB_STAGE_UNKNOWN) -
        (SUB_STAGE_ORDER[b.stage ?? ''] ?? SUB_STAGE_UNKNOWN)
      )
    case 'submitted':
      return submittedTs(a) - submittedTs(b)
    case 'age':
      return ageDays(a) - ageDays(b)
    default:
      return 0
  }
}

// Legacy stages only until the stage migration lands + the contract vocab swap
// (Inc-6) switches these to the new values. Enumerating STAGE_LABELS directly
// would leak the new-model options into the dropdown before any demand can be
// in them.
const STAGE_FILTER_KEYS: DemandStage[] = [
  'new',
  'scoping',
  'triaging',
  'resourcing',
  'parking',
  'closed',
]
const STAGE_FILTER_OPTIONS: { key: '' | DemandStage; label: string }[] = [
  { key: '', label: 'All stages' },
  ...STAGE_FILTER_KEYS.map((key) => ({ key, label: STAGE_LABELS[key] })),
]

export function MySubmissions() {
  const styles = useStyles()
  const navigate = useNavigate()
  const { showToast } = useToast()
  const { data, isLoading, error, refetch } = useDemands({
    mine_submissions: true,
    page_size: 500,
  })
  const allRows = useMemo(() => data?.items ?? [], [data])

  const [stageFilter, setStageFilter] = useState<'' | DemandStage>('')
  const { criteria, handleSort } = useColumnSort<SubSortKey>({ key: 'submitted', dir: 'desc' })
  const [exporting, setExporting] = useState(false)

  const rows = useMemo(() => {
    const filtered = stageFilter ? allRows.filter((d) => d.stage === stageFilter) : allRows
    return sortByCriteria(filtered, criteria, compareSubmission)
  }, [allRows, stageFilter, criteria])

  async function exportMine() {
    setExporting(true)
    try {
      const params: Record<string, string> = { mine_submissions: 'true' }
      if (stageFilter) params.stage = stageFilter
      const res = await api.get('/demands/export', { params, responseType: 'blob' })
      downloadBlob('my-submissions.xlsx', res.data as Blob)
    } catch (e) {
      showToast({ title: 'Export failed', body: await blobErrorMessage(e), intent: 'error' })
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <div>
          <TitleWithHelp topic="submissions">
            <Title2 block>My submissions</Title2>
          </TitleWithHelp>
        </div>
        <div className={styles.toolbar}>
          <Button
            appearance="primary"
            onClick={() => navigate('/submit-demand')}
            data-testid="my-submissions-new"
          >
            Submit a demand
          </Button>
        </div>
        {allRows.length > 0 && (
          <div className={styles.toolbar}>
            <Dropdown
              size="small"
              aria-label="Filter by stage"
              data-testid="my-submissions-stage-filter"
              value={stageFilter ? STAGE_LABELS[stageFilter] : 'All stages'}
              selectedOptions={[stageFilter]}
              onOptionSelect={(_, d) => setStageFilter((d.optionValue ?? '') as '' | DemandStage)}
            >
              {STAGE_FILTER_OPTIONS.map((o) => (
                <Option key={o.key || 'all'} value={o.key}>
                  {o.label}
                </Option>
              ))}
            </Dropdown>
            <Button
              appearance="subtle"
              size="small"
              icon={<ArrowDownload20Regular />}
              onClick={exportMine}
              disabled={exporting || rows.length === 0}
              data-testid="my-submissions-export"
              title="Export your submissions to Excel (.xlsx)"
            >
              {exporting ? 'Exporting…' : 'Export'}
            </Button>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className={styles.state} data-testid="my-submissions-loading">
          <Spinner size="tiny" /> Loading your submissions…
        </div>
      ) : error ? (
        <div data-testid="my-submissions-error">
          <ErrorCard
            title="Couldn't load your submissions"
            message={apiErrorMessage(error, 'Please try again.')}
            retry={() => {
              void refetch()
            }}
          />
        </div>
      ) : allRows.length === 0 ? (
        <div className={styles.state} data-testid="my-submissions-empty">
          You haven&apos;t submitted any demands yet.{' '}
          <Button
            appearance="transparent"
            size="small"
            onClick={() => navigate('/submit-demand')}
            data-testid="my-submissions-empty-new"
          >
            Submit your first
          </Button>
        </div>
      ) : rows.length === 0 ? (
        <div className={styles.state} data-testid="my-submissions-no-match">
          No submissions in this stage.
        </div>
      ) : (
        <div className={styles.wrap}>
          <Table aria-label="My submissions" data-testid="my-submissions-table" style={{ minWidth: 640 }}>
            <TableHeader>
              <TableRow>
                <SortableHeader sortKey="title" criteria={criteria} onSort={handleSort}>
                  Title
                </SortableHeader>
                <SortableHeader sortKey="fa" criteria={criteria} onSort={handleSort}>
                  Focus area
                </SortableHeader>
                <SortableHeader sortKey="stage" criteria={criteria} onSort={handleSort}>
                  Stage
                </SortableHeader>
                <SortableHeader sortKey="submitted" criteria={criteria} onSort={handleSort}>
                  Submitted
                </SortableHeader>
                <SortableHeader sortKey="age" criteria={criteria} onSort={handleSort}>
                  Stage age
                </SortableHeader>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((d) => (
                <TableRow
                  key={d.id}
                  className={styles.row}
                  onClick={() => navigate(`/demand/${d.id}`)}
                  data-testid={`my-submission-${d.id}`}
                >
                  <TableCell>
                    <Link to={`/demand/${d.id}`} onClick={(e) => e.stopPropagation()}>{d.title}</Link>
                  </TableCell>
                  {/* A request passed to the Digital Council has left for another
                      organisation. Its focus-area code and stage are OUR
                      bookkeeping from before the call, and showing them made
                      it look like any other row still in our hands (#1376). */}
                  {d.disposition === 'digital_council' ? (
                    <>
                      <TableCell>—</TableCell>
                      <TableCell>
                        <Badge appearance="tint" color="warning" data-testid={`my-submission-dc-${d.id}`}>
                          Passed to DSAI
                        </Badge>
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell>{focusAreaWithName(d.focus_area_code)}</TableCell>
                      <TableCell>
                        <Badge appearance="tint" color="informative">
                          {STAGE_LABELS[d.stage as DemandStage] ?? d.stage}
                        </Badge>
                      </TableCell>
                    </>
                  )}
                  <TableCell>{submittedDate(d)}</TableCell>
                  <TableCell>
                    {d.stage === 'closed' ? (
                      <Caption1 className={styles.muted}>—</Caption1>
                    ) : (
                      `${ageDays(d)}d`
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}
